// Showcase.js
// Hidden utility screen. Paste a roster blob — the `data` field from a
// leaderboard_submissions document, or just its players array — and it renders
// that team through the REAL Draft Complete screen in viewOnly mode. Nothing is
// written to Firestore (viewOnly skips the submit), so this is safe for
// featuring rosters other users drafted when no screenshot exists.
//
// Reached two ways: type /showcase, or tap the Home wordmark 5× quickly.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SPORT_CONFIG } from "../managers/draftManager";
import wordmark from "../assets/Rosterable.png";

const ACCENT = "#d4f53c";

// Firestore stores the sport as the capitalized iOS rawValue ("Football").
const RAW_TO_ID = {
  Football: "football", Baseball: "baseball", Basketball: "basketball",
  Hockey: "hockey", Soccer: "soccer",
};
const SPORT_OPTIONS = [
  { id: "football",   name: "Football" },
  { id: "baseball",   name: "Baseball" },
  { id: "basketball", name: "Basketball" },
  { id: "hockey",     name: "Hockey" },
  { id: "soccer",     name: "Soccer" },
];

// Build the sport object DraftComplete expects ({ id, name, hex }). Reuses the
// hex/positions already defined in SPORT_CONFIG so there's no second source.
function sportFromId(id) {
  if (!id || !SPORT_CONFIG[id]) return null;
  const name = SPORT_OPTIONS.find(s => s.id === id)?.name || id;
  return { id, name, hex: SPORT_CONFIG[id].color };
}

// Tolerant parse. Handles three shapes:
//   1. the full data object:  {"players":[...],"positions":[...],"sport":"Football","username":"X"}
//   2. a bare players array:  [{...},{...}]
//   3. a players-first blob copied without its outer braces:
//      [{...}],"username":"X","sport":"Football","positions":[...]}   (trailing quote tolerated)
function parseRoster(raw) {
  const t = (raw || "").trim();
  const noTrailQuote = t.replace(/"\s*$/, "");

  const attempts = [t];
  if (t.startsWith('"') && t.endsWith('"')) attempts.push(t.slice(1, -1));
  if (t.startsWith("[")) {
    attempts.push('{"players":' + noTrailQuote);
    attempts.push('{"players":' + noTrailQuote.replace(/^"/, ""));
  }

  for (const cand of attempts) {
    try {
      const parsed = JSON.parse(cand);
      const players = Array.isArray(parsed) ? parsed : parsed.players;
      if (Array.isArray(players) && players.length) {
        return {
          players,
          positions: Array.isArray(parsed.positions) ? parsed.positions : null,
          sport: parsed.sport || null,
          username: parsed.username || null,
        };
      }
    } catch { /* try the next repair */ }
  }
  return null;
}

export default function Showcase() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [sportOverride, setSportOverride] = useState("");
  const [error, setError] = useState("");

  function handleGenerate() {
    setError("");

    const parsed = parseRoster(text);
    if (!parsed) {
      setError("Couldn't read that. Paste the roster's data field (or its players array).");
      return;
    }

    const { players, positions, sport: sportRaw, username } = parsed;

    // Each player needs at least these for the score, chemistry, and cards.
    const bad = players.find(p => p == null || typeof p.overallRating !== "number" || !p.name || !p.city);
    if (bad !== undefined) {
      setError("Each player needs a name, a city, and a numeric overallRating.");
      return;
    }

    const sportId = sportOverride || RAW_TO_ID[sportRaw] || (sportRaw || "").toLowerCase();
    const sport = sportFromId(sportId);
    if (!sport) {
      setError("Pick a sport below — the roster didn't include a recognizable one.");
      return;
    }

    // Use the blob's positions when they line up; otherwise fall back to each
    // player's own position. DraftComplete re-derives slot labels for non-football.
    const draftPositions = positions && positions.length === players.length
      ? positions
      : players.map(p => p.position || "");

    navigate("/draft-complete", {
      state: { sport, draftedPlayers: players, draftPositions, viewOnly: true, username },
    });
  }

  return (
    <div style={styles.page}>
      <img src={wordmark} alt="Rosterable" style={styles.wordmark} />
      <div style={styles.title}>ROSTER SHOWCASE</div>
      <div style={styles.sub}>
        Paste a roster's <code style={styles.code}>data</code> field (or its players array) to render its
        Draft Complete view. Nothing is saved.
      </div>

      <textarea
        style={styles.textarea}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste roster JSON here…"
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
      />

      <div style={styles.row}>
        <span style={styles.label}>Sport</span>
        <select
          style={styles.select}
          value={sportOverride}
          onChange={(e) => setSportOverride(e.target.value)}
        >
          <option value="">Auto-detect from roster</option>
          {SPORT_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {error && <div style={styles.error}>⚠️ {error}</div>}

      <button style={styles.generateBtn} onClick={handleGenerate}>
        GENERATE DRAFT COMPLETE VIEW
      </button>
      <button style={styles.cancelBtn} onClick={() => navigate("/")}>
        CANCEL
      </button>
    </div>
  );
}

const styles = {
  page: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "16px 16px 40px",
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
  },
  wordmark: { height: 24, width: "auto", margin: "8px auto 18px", objectFit: "contain" },
  title: {
    fontSize: 20,
    fontWeight: 800,
    letterSpacing: 1,
    color: "#fff",
    textAlign: "center",
  },
  sub: {
    fontSize: 13,
    lineHeight: 1.5,
    color: "rgba(255,255,255,0.55)",
    textAlign: "center",
    margin: "8px auto 18px",
    maxWidth: 360,
  },
  code: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    background: "rgba(255,255,255,0.08)",
    padding: "1px 5px",
    borderRadius: 4,
    color: ACCENT,
  },
  textarea: {
    width: "100%",
    minHeight: 200,
    resize: "vertical",
    boxSizing: "border-box",
    padding: 12,
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    color: "#fff",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    lineHeight: 1.5,
    outline: "none",
  },
  row: { display: "flex", alignItems: "center", gap: 10, margin: "14px 0 4px" },
  label: { fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.7)" },
  select: {
    flex: 1,
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    color: "#fff",
    fontSize: 13,
    outline: "none",
  },
  error: {
    marginTop: 14,
    padding: "10px 12px",
    borderRadius: 10,
    background: "rgba(242,92,92,0.1)",
    border: "1px solid #f25c5c44",
    color: "#ffb4b4",
    fontSize: 13,
  },
  generateBtn: {
    marginTop: 20,
    padding: "14px 16px",
    borderRadius: 14,
    border: "none",
    background: ACCENT,
    color: "#0c0d10",
    fontSize: 15,
    fontWeight: 800,
    letterSpacing: 0.5,
    cursor: "pointer",
  },
  cancelBtn: {
    marginTop: 10,
    padding: "12px 16px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "transparent",
    color: "rgba(255,255,255,0.7)",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
};
